import { EmptyState, Pagination, SelectField, Table, type TableColumn } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { ErasureRowView, Option, Translate } from './compliance-types'

/**
 * Props for {@link ErasureRequestListView}.
 *
 * @property rows - The current page, newest first, already formatted for display.
 * @property statusFilter - The selected `ErasureStatus`, or `''` for all.
 * @property statusOptions - "All" plus the seven statuses.
 * @property error - True when the last request failed: renders the retry state.
 */
export interface ErasureRequestListViewProps {
	t: Translate
	rows: ErasureRowView[]
	statusFilter: string
	statusOptions: Option[]
	page: number
	pageCount: number
	error: boolean
	onStatusChange: (status: string) => void
	onPageChange: (page: number) => void
	onOpen: (id: string) => void
	onRetry: () => void
}

const columns = (t: Translate, onOpen: (id: string) => void): TableColumn<ErasureRowView>[] => [
	{
		key: 'customerId',
		header: t('deletions.columns.customer'),
		cell: (row) => (
			<Button variant="muted" size="sm" onPress={() => onOpen(row.id)}>
				{row.customerId}
			</Button>
		),
	},
	{ key: 'statusLabel', header: t('deletions.columns.status') },
	{ key: 'basisLabel', header: t('deletions.columns.basis') },
	{ key: 'requestedAt', header: t('deletions.columns.requested') },
	{ key: 'graceEndsAt', header: t('deletions.columns.graceEnds') },
]

/** The tenant's erasure requests: status filter, table and paging (organism). */
export function ErasureRequestListView(props: ErasureRequestListViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-4">
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('deletions.title')}</h2>
			<SelectField
				label={t('common.status')}
				value={props.statusFilter}
				onValueChange={props.onStatusChange}
				options={props.statusOptions.map((o) => ({ id: o.id, text: o.label }))}
			/>
			{props.error ? (
				<EmptyState title={t('deletions.error')} action={{ label: t('common.retry'), onPress: props.onRetry }} />
			) : (
				<Table
					aria-label={t('deletions.title')}
					columns={columns(t, props.onOpen)}
					rows={props.rows}
					emptyState={<EmptyState title={t('deletions.empty')} />}
				/>
			)}
			<Pagination page={props.page} pageCount={props.pageCount} onPageChange={props.onPageChange} />
		</section>
	)
}
